import { z } from 'zod';

export const publicRuntimeConfigSchema = z
    .object({
        version: z.literal(1),
        environment: z.enum(['development', 'production']),
        googleAnalyticsTrackingId: z.string().trim().min(1).optional(),
        logoDevApiKey: z.string().trim().min(1).optional(),
        posthogApiKey: z.string().trim().min(1).optional(),
        posthogHost: z.string().url().optional(),
        sentryDsn: z.string().url().optional(),
        zapierEmbedClientId: z.string().trim().min(1).optional(),
    })
    .strict();

export type PublicRuntimeConfig = z.infer<typeof publicRuntimeConfigSchema>;

const fallbackRuntimeConfig: PublicRuntimeConfig = {
    environment: import.meta.env.PROD ? 'production' : 'development',
    version: 1,
};

let runtimeConfig: PublicRuntimeConfig = fallbackRuntimeConfig;
let runtimeConfigPromise: Promise<PublicRuntimeConfig> | undefined;

const runtimeConfigUrl = import.meta.env.PROD
    ? 'https://api.countercyclical.io/public/services/runtime-config'
    : '/public/services/runtime-config';

const fetchRuntimeConfig = async (): Promise<PublicRuntimeConfig> => {
    const response = await fetch(runtimeConfigUrl, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
        throw new Error(`Runtime configuration request failed with status ${response.status}.`);
    }

    return publicRuntimeConfigSchema.parse(await response.json());
};

export const loadPublicRuntimeConfig = () => {
    runtimeConfigPromise ??= fetchRuntimeConfig()
        .then((config) => {
            runtimeConfig = config;
            return config;
        })
        .catch(() => fallbackRuntimeConfig);

    return runtimeConfigPromise;
};

export const getPublicRuntimeConfig = (): Readonly<PublicRuntimeConfig> => runtimeConfig;

export const resetPublicRuntimeConfigForTests = () => {
    if (!import.meta.env.DEV) {
        throw new Error('Runtime configuration can only be reset outside production.');
    }

    runtimeConfig = fallbackRuntimeConfig;
    runtimeConfigPromise = undefined;
};
